import { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, TrendingUp, GraduationCap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Top10USA2026Page() {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUSAUniversities = async () => {
      try {
        const response = await axios.get(`${API}/universities?country=USA&limit=20`);
        const sorted = response.data.sort((a, b) => a.ranking_global - b.ranking_global).slice(0, 10);
        setUniversities(sorted);
      } catch (error) {
        console.error('Error fetching universities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUSAUniversities();
  }, []);

  return (
    <div className="py-12 bg-white">
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        {/* SEO Header */}
        <div className="max-w-4xl mx-auto mb-12">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-foreground mb-4">
            Top 10 Universities in USA (2026 Rankings)
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed mb-6">
            Discover the highest-ranked American universities for international students in 2026. These institutions offer world-class education, cutting-edge research facilities, and unparalleled career opportunities.
          </p>
          <div className="bg-secondary rounded-sm p-6">
            <h2 className="text-xl font-playfair font-bold mb-3">Why Study in the USA?</h2>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-accent font-bold">•</span>
                <span><strong>Academic Excellence:</strong> Home to 8 of the world's top 10 universities</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent font-bold">•</span>
                <span><strong>Research Opportunities:</strong> Access to cutting-edge labs and Nobel Prize-winning faculty</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent font-bold">•</span>
                <span><strong>Career Prospects:</strong> Strong industry connections and Optional Practical Training (OPT)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent font-bold">•</span>
                <span><strong>Diverse Campus Life:</strong> Vibrant student communities from 195+ countries</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Rankings List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-secondary animate-pulse rounded-sm" />
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {universities.map((uni, idx) => (
              <article key={uni.university_id} className="border border-border/40 bg-card shadow-sm hover:shadow-md transition-all rounded-sm overflow-hidden">
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-1/3 relative">
                    <img
                      src={uni.banner_image}
                      alt={uni.name}
                      className="w-full h-64 md:h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-accent text-accent-foreground w-16 h-16 rounded-sm flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-2xl font-playfair font-bold">#{idx + 1}</div>
                      </div>
                    </div>
                  </div>
                  <div className="md:w-2/3 p-6">
                    <div className="flex items-start gap-3 mb-3">
                      <img src={uni.logo} alt={`${uni.name} logo`} className="w-12 h-12 rounded-sm object-cover" />
                      <div className="flex-1">
                        <h3 className="text-2xl font-playfair font-bold text-foreground mb-1">{uni.name}</h3>
                        <p className="text-sm text-muted-foreground">{uni.region}</p>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground">Global Rank</div>
                        <div className="text-lg font-bold text-accent">#{uni.ranking_global}</div>
                      </div>
                    </div>

                    <p className="text-muted-foreground leading-relaxed mb-4">{uni.description}</p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      <div className="border border-border/40 rounded-sm p-2 text-center">
                        <div className="text-xs text-muted-foreground mb-1">Acceptance</div>
                        <div className="text-sm font-bold">{uni.acceptance_rate}%</div>
                      </div>
                      <div className="border border-border/40 rounded-sm p-2 text-center">
                        <div className="text-xs text-muted-foreground mb-1">Tuition</div>
                        <div className="text-sm font-bold">${(uni.tuition_min/1000).toFixed(0)}k-${(uni.tuition_max/1000).toFixed(0)}k</div>
                      </div>
                      <div className="border border-border/40 rounded-sm p-2 text-center col-span-2">
                        <div className="text-xs text-muted-foreground mb-1">Top Programs</div>
                        <div className="text-xs font-medium">{uni.programs.slice(0, 2).join(', ')}</div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Link to={`/universities/${uni.university_id}`}>
                        <Button size="sm" className="rounded-sm">View Full Profile</Button>
                      </Link>
                      <Button size="sm" variant="outline" className="rounded-sm" onClick={() => window.open(uni.website, '_blank')}>
                        Visit Website
                      </Button>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Additional Info */}
        <div className="mt-12 bg-primary text-primary-foreground rounded-sm p-8">
          <h2 className="text-2xl md:text-3xl font-playfair font-bold mb-4">Application Tips for Top US Universities</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-bold mb-2">Ivy League Admissions</h3>
              <ul className="space-y-2 text-primary-foreground/90 text-sm">
                <li>• Start preparing 12-18 months in advance</li>
                <li>• Aim for SAT 1500+ or ACT 34+</li>
                <li>• Strong extracurriculars and leadership roles</li>
                <li>• Compelling personal essays</li>
                <li>• Early Decision/Action can improve chances</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2">Financial Aid & Scholarships</h3>
              <ul className="space-y-2 text-primary-foreground/90 text-sm">
                <li>• Many offer need-blind admissions</li>
                <li>• Full financial aid available at top schools</li>
                <li>• Merit scholarships for exceptional students</li>
                <li>• Average aid package: $50,000-$70,000/year</li>
                <li>• International students eligible at most schools</li>
              </ul>
            </div>
          </div>
          <div className="mt-6 text-center">
            <Link to="/scholarships">
              <Button size="lg" variant="secondary" className="rounded-sm">
                Explore Scholarship Opportunities
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}